import { useState, MouseEvent, ChangeEvent, useEffect } from 'react'
import EnhancedTable from '@common/components/Table/EnhancedTable'
import EnhancedTableBodyTaskManager from '../components/EnhancedTableBodyTaskManager'
import { HeadCell } from '@common/components/Table/table.types'
import { Task } from '@common/config/api/client'
import { ROW_PER_PAGE_OPTIONS } from '@common/constants/table'
import { useUserList } from '@qc_task_list_view/hooks/useUserList'
import { useTabOptionalComponent } from '@common/hooks/useTabOptionalComponent'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import PopupAssignMenuList from '@qc_task_list_view/components/PopupAssignMenuList'
import { UNSELECATBLE_TASK_STATUS } from '@qc_task_list_view/constant'
import { useParams } from 'react-router-dom'
import { useQCTasksManagementHandlers } from '../hooks/useQCTasksManagementHandlers'
import { Stack, Typography, TableBody, TableRow, TableCell } from '@mui/material'
import { SPACING } from '@common/theme/spacing'
import { ExtendedTask } from './QcTaskListTableCompletedTasks'

interface QcTaskListTableTaskManagerProps {
  subjectId?: string
}

const headCells: readonly HeadCell<ExtendedTask>[] = [
  { id: 'task_pseudo_id', disablePadding: true, label: 'Task Pseudo ID', disableSort: false },
  { id: 'sites', disablePadding: false, label: 'Site ID', disableSort: false },
  { id: 'subject_id', disablePadding: false, label: 'Subject ID', disableSort: false },
  { id: 'subject_blind_code', disablePadding: false, label: 'Subject Blind Code', disableSort: true },
  { id: 'timepoint_requirement', disablePadding: false, label: 'Timepoint', disableSort: false },
  { id: 'task_type', disablePadding: false, label: 'Task Type', disableSort: false },
  { id: 'task_state', disablePadding: false, label: 'Task Status', disableSort: false },
  { id: 'query_status', disablePadding: false, label: 'Query Status', disableSort: false },
  { id: 'create_date', disablePadding: false, label: 'Created At', disableSort: false },
  { id: 'assignee', disablePadding: false, label: 'Assignee', disableSort: false },
]

function EmptyTaskManagerTable() {
  return (
    <Stack spacing={SPACING.minSpacing} alignItems="center" sx={{ py: SPACING.spacing2xl }}>
      <Typography variant="h6">No tasks available to manage</Typography>
      <Typography variant="body2" color="text.secondary">
        There are currently no tasks in the system to manage
      </Typography>
    </Stack>
  )
}

export default function QcTaskListTableTaskManager({ subjectId }: QcTaskListTableTaskManagerProps) {
  const [selected, setSelected] = useState<readonly (string | null | undefined)[]>([])
  const { setOptionalComponent, setIsComponentVisible } = useTabOptionalComponent()
  const { user } = useExtendedAuth()
  const { studyId } = useParams()

  const {
    order,
    orderBy,
    page,
    paginationParams,
    data: qcListData,
    error: qcListError,
    isLoading: qclistLoading,
    handleRequestSort,
    handlePageChange,
    handleRowsPerPageChange,
  } = useQCTasksManagementHandlers({
    category: 'task_manager',
    studyId: studyId!,
    subjectId,
  })

  const {
    data: userListData,
    error: userListError,
    isLoading: UserlistLoading,
  } = useUserList(['QCTasksList', 'Users'], { study_id: studyId! })

  useEffect(() => {
    setSelected([])
  }, [qcListData, userListData])

  useEffect(() => {
    if (selected.length > 0) {
      const selectedTasks = qcListData?.data?.items.filter((task) => selected.includes(task.task_id))

      setIsComponentVisible(true)
      setOptionalComponent(
        <PopupAssignMenuList
          tasksAndAssignees={selectedTasks?.map((task) => ({ taskId: task.task_id, assignee: task.assignee })) || []}
          currentAuthedUserId={user?.profile.sub || ''}
          assigneeList={userListData?.data?.users || []}
          isBulkAssign={true}
        />,
      )
    } else {
      setIsComponentVisible(false)
      setOptionalComponent(null)
    }
  }, [qcListData, userListData, selected])

  if (qclistLoading || UserlistLoading) {
    return <div>Loading...</div>
  }
  if (qcListError || userListError) {
    return <div>Error: {(qcListError && qcListError.message) || (userListError && userListError.message)}</div>
  }

  const hasTasks = qcListData?.data?.items && qcListData.data.items.length > 0

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = qcListData?.data?.items
        .filter((task) => !UNSELECATBLE_TASK_STATUS.includes(task.task_state))
        .map((n: Task) => n.task_id)
      if (!newSelected || (newSelected && newSelected.length > 0 && newSelected.length === selected.length)) {
        setSelected([])
        return
      }
      setSelected(newSelected)
    } else {
      setSelected([])
    }
  }

  const handleClick = (_event: MouseEvent<unknown>, id?: string | null) => {
    const selectedIndex = selected.indexOf(id)
    let newSelected: readonly (string | null | undefined)[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1))
    }
    setSelected(newSelected)
  }

  return (
    <EnhancedTable<ExtendedTask>
      rows={qcListData?.data?.items || []}
      headCells={headCells}
      order={order}
      orderBy={orderBy.split('.')[0] as keyof Task}
      selected={selected}
      rowsPerPage={paginationParams.limit || 0}
      rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
      page={page}
      next_cursor={qcListData?.data?.pagination.next_cursor}
      previous_cursor={qcListData?.data?.pagination.previous_cursor}
      onRequestSort={handleRequestSort}
      onSelectAllClick={handleSelectAllClick}
      onChangePage={handlePageChange}
      onChangeRowsPerPage={handleRowsPerPageChange}
    >
      {hasTasks ? (
        <EnhancedTableBodyTaskManager
          rows={qcListData?.data?.items || []}
          order={order}
          orderBy={orderBy}
          selected={selected}
          handleClick={handleClick}
          assigneeList={userListData?.data?.users}
          authedUserId={user?.profile.sub}
        />
      ) : (
        <TableBody>
          <TableRow>
            <TableCell colSpan={headCells.length}>
              <EmptyTaskManagerTable />
            </TableCell>
          </TableRow>
        </TableBody>
      )}
    </EnhancedTable>
  )
}
